import { Language } from '../translations';

export const siteUrl = 'https://metmaat.com';

/**
 * Social sharing card (WhatsApp, LinkedIn, Slack, iMessage, X).
 * 1200x630 — generated by scripts/make-og-image.mjs.
 * Bump the ?v= query when the image changes: WhatsApp and LinkedIn
 * cache preview images per URL and will otherwise keep the old one.
 */
export const ogImage = `${siteUrl}/og-image.jpg?v=1`;
export const ogImageWidth = 1200;
export const ogImageHeight = 630;
export const ogImageAlt = 'Met Maät — Software Architecture & Technical Leadership';

export interface RouteSeo {
    path: string;
    title: Record<Language, string>;
    description: Record<Language, string>;
    /** Listed in sitemap.xml and indexable; false adds noindex. */
    indexable: boolean;
}

/**
 * Single source of truth for per-route SEO metadata.
 * Used at runtime by components/Seo.tsx and at build time by
 * scripts/prerender.mjs.
 *
 * Every route in App.tsx must have an entry here — prerender.mjs fails the
 * build if one is missing, because an unprerendered route would 404 on a
 * direct visit (see firebase.json).
 */
export const routeSeo: RouteSeo[] = [
    {
        path: '/',
        title: {
            en: 'Met Maät — Software Architecture & Technical Leadership',
            nl: 'Met Maät — Softwarearchitectuur & Technisch Leiderschap',
        },
        description: {
            en: 'Software architecture and technical leadership consultancy in the Netherlands. We bring structure, quality, and direction to teams building software — hands-on and from experience.',
            nl: 'Consultancy voor softwarearchitectuur en technisch leiderschap. We brengen structuur, kwaliteit en richting aan teams die software bouwen — hands-on en vanuit ervaring.',
        },
        indexable: true,
    },
    {
        path: '/services',
        title: {
            en: 'Services — Architecture, Team Guidance & Project Recovery | Met Maät',
            nl: 'Diensten — Architectuur, Teambegeleiding & Project Recovery | Met Maät',
        },
        description: {
            en: 'Software architecture, backend & cloud development, AI adoption, technical coaching, and project recovery. Direction, quality, and efficiency in software development.',
            nl: 'Softwarearchitectuur, backend- en clouddevelopment, AI-adoptie, technische coaching en project recovery. Richting, kwaliteit en efficiëntie in softwareontwikkeling.',
        },
        indexable: true,
    },
    {
        path: '/methodology',
        title: {
            en: 'Methodology — A Structured Engineering Approach | Met Maät',
            nl: 'Werkwijze — Een Gestructureerde Engineering-aanpak | Met Maät',
        },
        description: {
            en: 'Our methodology: engineered process over improvisation, simplicity, and future-proof (AI-ready) design — based on the IDesign method for software architecture.',
            nl: 'Onze werkwijze: een engineered proces in plaats van improvisatie, eenvoud en toekomstbestendig (AI-ready) ontwerp — gebaseerd op de IDesign-methode voor softwarearchitectuur.',
        },
        indexable: true,
    },
    {
        path: '/about',
        title: {
            en: 'About — 14+ Years of Software Experience | Met Maät',
            nl: 'Over ons — 14+ Jaar Software-ervaring | Met Maät',
        },
        description: {
            en: 'Met Maät is the software consultancy of Ben Degens: 14+ years of building and guiding software at leading organizations, from architecture to hands-on engineering.',
            nl: 'Met Maät is de software-consultancy van Ben Degens: ruim 14 jaar software bouwen en begeleiden bij toonaangevende organisaties, van architectuur tot hands-on engineering.',
        },
        indexable: true,
    },
    {
        path: '/contact',
        title: {
            en: 'Contact | Met Maät',
            nl: 'Contact | Met Maät',
        },
        description: {
            en: 'Get in touch with Met Maät for software architecture, technical leadership, or project recovery. Based in Klimmen, the Netherlands.',
            nl: 'Neem contact op met Met Maät voor softwarearchitectuur, technisch leiderschap of project recovery. Gevestigd in Klimmen, Nederland.',
        },
        indexable: true,
    },
    {
        path: '/disclaimer',
        title: {
            en: 'Disclaimer | Met Maät',
            nl: 'Disclaimer | Met Maät',
        },
        description: {
            en: 'Disclaimer for the Met Maät website.',
            nl: 'Disclaimer voor de website van Met Maät.',
        },
        indexable: false,
    },
    {
        path: '/privacy-statement',
        title: {
            en: 'Privacy Statement | Met Maät',
            nl: 'Privacyverklaring | Met Maät',
        },
        description: {
            en: 'Privacy statement for the Met Maät website.',
            nl: 'Privacyverklaring voor de website van Met Maät.',
        },
        indexable: false,
    },
];

/**
 * The catch-all route. Rendered to dist/404.html, which Firebase Hosting
 * serves with a real HTTP 404 for unknown paths.
 */
export const notFoundSeo: RouteSeo = {
    path: '/404',
    title: {
        en: 'Page not found | Met Maät',
        nl: 'Pagina niet gevonden | Met Maät',
    },
    description: {
        en: 'This page does not exist (anymore).',
        nl: 'Deze pagina bestaat niet (meer).',
    },
    indexable: false,
};

export const getRouteSeo = (pathname: string): RouteSeo => {
    const clean = pathname.replace(/\/+$/, '') || '/';
    return routeSeo.find((r) => r.path === clean) ?? notFoundSeo;
};
